import { supabase } from "@/shared/lib/supabaseClient";
import type { Database } from "@/shared/types/database.types";

export type PreferenciaNotificacao =
  Database["public"]["Tables"]["preferencias_notificacao"]["Row"];

/**
 * Camada de dados de `preferencias_notificacao` (Frente 2, migration 0070).
 *
 * Semântica opt-out (aprovada 03/09/2026, já implementada no dispatcher —
 * supabase/functions/notificacoes-dispatcher/index.ts): AUSÊNCIA de linha
 * para uma (pessoa, categoria, canal) significa "recebe" — só uma linha
 * explícita com `ativo=false` bloqueia o envio. Esta camada segue a mesma
 * regra: a UI trata a ausência de linha como "ativo".
 *
 * Hoje só existe o canal "push" (CHECK constraint da migration 0070); e-mail/
 * WhatsApp/SMS futuros entram como novo valor permitido no CHECK do banco,
 * não como coluna nova — nada aqui precisa mudar quando isso acontecer além
 * de expor o novo canal na tela.
 */
export const preferenciasNotificacaoService = {
  async listarDaPessoa(pessoaId: string): Promise<PreferenciaNotificacao[]> {
    const { data, error } = await supabase
      .from("preferencias_notificacao")
      .select("*")
      .eq("pessoa_id", pessoaId)
      .eq("canal", "push");
    if (error) throw new Error("Não foi possível carregar as preferências de notificação.");
    return data ?? [];
  },

  /**
   * Define a preferência de uma categoria (liga/desliga). Usa upsert com
   * `onConflict` na constraint única (pessoa_id, categoria, canal) — segura
   * mesmo com upsert porque a constraint já inclui `pessoa_id`, então não há
   * risco de conflito entre pessoas diferentes (diferente do caso de
   * `push_subscriptions.endpoint`, que é único GLOBALMENTE).
   */
  async definirPreferencia(pessoaId: string, categoria: string, ativo: boolean): Promise<void> {
    const { error } = await supabase.from("preferencias_notificacao").upsert(
      {
        pessoa_id: pessoaId,
        categoria,
        canal: "push",
        ativo,
      },
      { onConflict: "pessoa_id,categoria,canal" },
    );
    if (error) throw new Error("Não foi possível salvar a preferência de notificação.");
  },
};
